// Backlog List Page - Drag-and-drop backlog item management

import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import type { DropResult } from '@hello-pangea/dnd';
import { PrimaryButton, DefaultButton, Label, Spinner, MessageBar, MessageBarType, useTheme } from '@fluentui/react';
import { v4 as uuidv4 } from 'uuid';
import { useBacklog } from '../BacklogContext';
import EditableField from '../components/EditableField';
import type { BacklogItem } from '../types';

const BacklogListPage: React.FC = () => {
  const theme = useTheme();
  const { id } = useParams<{ id: string }>();
  const { currentBacklog, loading, error, fetchBacklog, updateItem, deleteItem, reorderItems } = useBacklog();
  const [backlogItems, setBacklogItems] = useState<BacklogItem[]>([]);

  useEffect(() => {
    if (id) {
      fetchBacklog(Number(id));
    }
  }, [id, fetchBacklog]);

  useEffect(() => {
    if (currentBacklog?.items) {
      // Sort items by position
      const sortedItems = [...currentBacklog.items].sort((a, b) => a.position - b.position);
      setBacklogItems(sortedItems);
    }
  }, [currentBacklog]);

  const handleAddItem = () => {
    const newItem: BacklogItem = {
      id: uuidv4(),
      order: backlogItems.length + 1,
      title: `Item ${backlogItems.length + 1}`,
      description: '',
      notes: [],
      status: 'Not Started',
      requestedBy: '',
      lastModified: new Date(),
      lastModifiedBy: '',
      createdOn: new Date(),
      createdBy: '',
      position: backlogItems.length,
    };
    setBacklogItems([...backlogItems, newItem]);
  };

  const handleRemoveItem = async (itemId: string) => {
    if (!id) return;
    
    try {
      await deleteItem(Number(id), itemId);
      setBacklogItems(backlogItems.filter((item) => item.id !== itemId));
    } catch (err) {
      console.error('Failed to delete item:', err);
    }
  };

  const handleDragEnd = async (result: DropResult) => {
    if (!result.destination || !id) return;

    const items = Array.from(backlogItems);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    // Update positions
    const updatedItems = items.map((item, index) => ({
      ...item,
      position: index,
      order: index + 1,
    }));

    setBacklogItems(updatedItems);

    try {
      await reorderItems(Number(id), updatedItems);
    } catch (err) {
      console.error('Failed to reorder items:', err);
      // Revert on error
      setBacklogItems(backlogItems);
    }
  };

  const handleFieldChange = async (item: BacklogItem, field: keyof BacklogItem, value: string) => {
    if (!id) return;

    const updatedItem = {
      ...item,
      [field]: value,
      lastModified: new Date(),
    };

    // Optimistically update UI
    setBacklogItems(
      backlogItems.map((i) => (i.id === item.id ? updatedItem : i))
    );

    try {
      await updateItem(Number(id), updatedItem);
    } catch (err) {
      console.error('Failed to update item:', err);
      // Revert on error
      setBacklogItems(backlogItems);
    }
  };

  if (loading && !currentBacklog) {
    return (
      <div style={{ padding: '20px', textAlign: 'center' }}>
        <Spinner label="Loading backlog..." />
      </div>
    );
  }

  return (
    <div style={{ padding: '20px', maxWidth: '1200px', margin: '0 auto' }}>
      <div style={{ marginBottom: '20px' }}>
        <h1 style={{ color: theme.palette.neutralPrimary, marginBottom: '10px' }}>
          {currentBacklog?.title || 'Backlog'}
        </h1>
        <PrimaryButton onClick={handleAddItem}>Add Item</PrimaryButton>
      </div>

      {error && (
        <MessageBar
          messageBarType={MessageBarType.error}
          onDismiss={() => {}}
          dismissButtonAriaLabel="Close"
          style={{ marginBottom: '20px' }}
        >
          {error}
        </MessageBar>
      )}

      <DragDropContext onDragEnd={handleDragEnd}>
        <Droppable droppableId="backlog">
          {(provided, snapshot) => (
            <div
              {...provided.droppableProps}
              ref={provided.innerRef}
              style={{
                backgroundColor: snapshot.isDraggingOver
                  ? theme.palette.neutralLighter
                  : 'transparent',
                minHeight: '400px',
              }}
            >
              {backlogItems.map((item, index) => (
                <Draggable key={item.id} draggableId={String(item.id)} index={index}>
                  {(provided, snapshot) => (
                    <div
                      ref={provided.innerRef}
                      {...provided.draggableProps}
                      {...provided.dragHandleProps}
                      style={{
                        userSelect: 'none',
                        padding: '16px',
                        margin: '0 0 8px 0',
                        minHeight: '50px',
                        backgroundColor: snapshot.isDragging
                          ? theme.palette.neutralLight
                          : theme.palette.white,
                        border: `1px solid ${theme.palette.neutralLight}`,
                        borderRadius: '4px',
                        boxShadow: snapshot.isDragging
                          ? theme.effects.elevation8
                          : theme.effects.elevation4,
                        ...provided.draggableProps.style,
                      }}
                    >
                      <div>
                        <Label style={{ color: theme.palette.neutralPrimary }}>Title</Label>
                        <EditableField
                          value={item.title}
                          onChange={(newValue) => handleFieldChange(item, 'title', newValue)}
                          placeholder="Enter title..."
                        />
                        <Label style={{ color: theme.palette.neutralPrimary, marginTop: '12px' }}>
                          Description
                        </Label>
                        <EditableField
                          value={item.description}
                          onChange={(newValue) => handleFieldChange(item, 'description', newValue)}
                          placeholder="Enter description..."
                          multiline
                          rows={3}
                        />
                        <div style={{ marginTop: '12px' }}>
                          <DefaultButton onClick={() => handleRemoveItem(item.id)}>Remove</DefaultButton>
                        </div>
                      </div>
                    </div>
                  )}
                </Draggable>
              ))}
              {provided.placeholder}
            </div>
          )}
        </Droppable>
      </DragDropContext>

      {backlogItems.length === 0 && !loading && (
        <div
          style={{
            textAlign: 'center',
            padding: '40px',
            color: theme.palette.neutralSecondary,
          }}
        >
          No items yet. Click "Add Item" to get started.
        </div>
      )}
    </div>
  );
};

export default BacklogListPage;
